import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { authenticatedFetch } from '../../utils/api';
import GaugeChip from '../GaugeChip';
import DiaCircle from '../DiaCircle';
import ProgressBar from '../ProgressBar';
import { formatInteger } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
  count: number;
  created_at: string;
  updated_at: string;
}

interface Program {
  program_id: number;
  company_name: string;
  company_id: number;
  status: string;
  has_shift_details: boolean;
  total_weight: number | null;
  completed_weight: number | null;
  remaining_weight: number | null;
  progress_percentage: number | null;
  total_rolls: number | null;
  completed_rolls: number;
  remaining_rolls: number | null;
  rolls_progress_percentage: number | null;
  created_at: string;
  updated_at: string;
}

interface EmployeeShiftDetail {
  id: number;
  program_id: number;
  program_lot: string | null;
  company_name: string;
  company_id: number;
  dia: string;
  gauge: string;
  machine_type: string;
  rolls: number;
  weight: number;
  shift_date: string;
  shift_time: string;
  shift_time_display: string;
  employee_name: string;
  employee_id: number;
  employee_shift_id: number;
  progress_info: {
    total_weight: number;
    completed_weight: number;
    remaining_weight: number;
    progress_percentage: number;
  } | null;
}

const machineSchema = z.object({
  dia: z.number().min(0, 'Diameter must be a positive number'),
  gauge: z.enum(['14', '16', '24'], {
    required_error: 'Gauge is required',
  }),
  machine_type: z.string().min(1, 'Type is required'),
  count: z.number().min(1, 'Count must be at least 1')
});

type MachineFormData = z.infer<typeof machineSchema>;

export default function MachineDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [machine, setMachine] = useState<Machine | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [employeeShifts, setEmployeeShifts] = useState<EmployeeShiftDetail[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);

  const { control, handleSubmit, reset } = useForm<MachineFormData>({
    resolver: zodResolver(machineSchema),
    defaultValues: {
      dia: 0,
      gauge: '' as any,
      machine_type: '',
      count: 1
    }
  });

  useEffect(() => {
    fetchMachine();
    fetchPrograms();
    fetchEmployeeShifts();
  }, [id]);

  const fetchMachine = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/machines/${id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch machine');
      }

      const data = await response.json();
      setMachine(data);
      reset({
        dia: Number(data.dia),
        gauge: data.gauge,
        machine_type: data.machine_type,
        count: data.count || 1
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    setProgramsLoading(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/machines/${id}/programs`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch programs');
      }

      const data = await response.json();
      setPrograms(data);
    } catch (err) {
      console.error('Error fetching programs:', err);
    } finally {
      setProgramsLoading(false);
    }
  };

  const fetchEmployeeShifts = async () => {
    setShiftsLoading(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/machines/${id}/employee_shifts`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch employee shifts');
      }

      const data = await response.json();
      setEmployeeShifts(data);
    } catch (err) {
      console.error('Error fetching employee shifts:', err);
    } finally {
      setShiftsLoading(false);
    }
  };

  const onSubmit = async (data: MachineFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/machines/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ machine: data }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      const updatedMachine = await response.json();
      setMachine(updatedMachine);
      setIsEditing(false);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this machine?')) {
      try {
        const response = await authenticatedFetch(`${API_URL}/machines/${id}`, {
          method: 'DELETE',
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete machine');
        }

        navigate('/machines');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            mb: 3, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography variant={isSmallMobile ? "h6" : "h5"} component="h2">
              {isEditing ? 'Edit Machine' : 'Machine Details'}
            </Typography>
            {isSmallMobile ? (
              <IconButton
                onClick={() => navigate('/machines')}
                color="primary"
                sx={{ alignSelf: 'flex-start' }}
              >
                <ArrowBackIcon />
              </IconButton>
            ) : (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/machines')}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
              >
                Back
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {isEditing ? (
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="dia"
                control={control}
                render={({ field: { onChange, value, ...field }, fieldState }) => (
                  <TextField
                    {...field}
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    label="Diameter"
                    type="number"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="gauge"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl 
                    fullWidth 
                    margin="normal" 
                    error={!!fieldState.error}
                  >
                    <FormLabel>Gauge</FormLabel>
                    <RadioGroup {...field} row>
                      <FormControlLabel 
                        value="14" 
                        control={<Radio />} 
                        label="14 GG" 
                      />
                      <FormControlLabel 
                        value="16" 
                        control={<Radio />} 
                        label="16 GG" 
                      />
                      <FormControlLabel 
                        value="24" 
                        control={<Radio />} 
                        label="24 GG" 
                      />
                    </RadioGroup>
                    {fieldState.error && (
                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="machine_type"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl 
                    fullWidth 
                    margin="normal" 
                    error={!!fieldState.error}
                  >
                    <FormLabel>Type</FormLabel>
                    <RadioGroup {...field} row>
                      <FormControlLabel 
                        value="rib" 
                        control={<Radio />} 
                        label="Rib" 
                      />
                      <FormControlLabel 
                        value="fine" 
                        control={<Radio />} 
                        label="Fine" 
                      />
                    </RadioGroup>
                    {fieldState.error && (
                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="count"
                control={control}
                render={({ field: { onChange, value, ...field }, fieldState }) => (
                  <TextField
                    {...field}
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    label="Count (Number of Machines)"
                    type="number"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || "Number of machines with this configuration"}
                    inputProps={{ min: 1 }}
                  />
                )}
              />

              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                sx={{ mt: 3 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  fullWidth
                  size={isSmallMobile ? "medium" : "large"}
                >
                  {isSmallMobile ? 'Save' : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                  fullWidth
                  size={isSmallMobile ? "medium" : "large"}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Diameter
              </Typography>
              <Box sx={{ mb: 2 }}>
                {machine && (
                  <DiaCircle
                    dia={machine.dia}
                    gauge={machine.gauge}
                    machineType={machine.machine_type}
                    machines={[machine]}
                    size="small"
                  />
                )}
              </Box>

              <Typography variant="subtitle2" color="text.secondary">
                Gauge
              </Typography>
              <Box sx={{ mb: 2 }}>
                {machine?.gauge && <GaugeChip gauge={machine.gauge} />}
              </Box>

              <Typography variant="subtitle2" color="text.secondary">
                Type
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={machine?.machine_type} 
                  size="small"
                  sx={{
                    bgcolor: machine?.machine_type === 'rib' ? '#e0e0e0' : '#757575',
                    color: machine?.machine_type === 'rib' ? '#000' : '#fff',
                  }}
                />
              </Box>

              <Typography variant="subtitle2" color="text.secondary">
                Count (Number of Machines)
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={machine?.count || 1} 
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              </Box>

              <Typography variant="subtitle2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {new Date(machine?.created_at || '').toLocaleDateString()}
              </Typography>

              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2}
              >
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditing(true)}
                  fullWidth
                  size={isSmallMobile ? "medium" : "large"}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  fullWidth
                  size={isSmallMobile ? "medium" : "large"}
                >
                  Delete
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Current Programs Section */}
      <Card sx={{ mt: { xs: 2, sm: 3 }, mb: 4 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant={isSmallMobile ? "subtitle1" : "h6"} component="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
            Current Programs {!isSmallMobile && "(In Progress & Not Started)"}
          </Typography>
          
          {programsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
              <CircularProgress size={30} />
            </Box>
          ) : programs.length === 0 ? (
            <Alert severity="info">
              No current programs assigned to this machine.
            </Alert>
          ) : isMobile ? (
            // Mobile card-based layout
            <Stack spacing={2}>
              {programs.map((program) => (
                <Paper 
                  key={program.program_id} 
                  variant="outlined" 
                  sx={{ 
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => navigate(`/programs/${program.program_id}`)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {program.company_name}
                      </Typography>
                      <Chip
                        label={program.status.replace('_', ' ').toUpperCase()}
                        size="small"
                        color={program.status === 'in_progress' ? 'primary' : 'default'}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/programs/${program.program_id}`);
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Progress Bars */}
                  <Stack spacing={1.5}>
                    <ProgressBar
                      type="weight"
                      done={typeof program.completed_weight === 'number' ? program.completed_weight : 0}
                      total={typeof program.total_weight === 'number' ? program.total_weight : 0}
                      size="small"
                    />
                    {program.total_rolls && program.total_rolls > 0 && (
                      <ProgressBar
                        type="rolls"
                        done={program.completed_rolls ?? 0}
                        total={program.total_rolls}
                        remainingWeight={program.remaining_weight ?? 0}
                        size="small"
                      />
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            // Desktop table layout
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Company</strong></TableCell>
                    <TableCell><strong>Weight Progress</strong></TableCell>
                    <TableCell><strong>Rolls Progress</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {programs.map((program) => (
                    <TableRow key={program.program_id}>
                      <TableCell>
                        <Chip
                          label={program.status.replace('_', ' ').toUpperCase()}
                          size="small"
                          color={program.status === 'in_progress' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{program.company_name}</TableCell>
                      <TableCell>
                        <ProgressBar
                          type="weight"
                          done={typeof program.completed_weight === 'number' ? program.completed_weight : 0}
                          total={typeof program.total_weight === 'number' ? program.total_weight : 0}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {program.total_rolls && program.total_rolls > 0 ? (
                          <ProgressBar
                            type="rolls"
                            done={program.completed_rolls ?? 0}
                            total={program.total_rolls}
                            remainingWeight={program.remaining_weight ?? 0}
                            size="small"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not specified
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/programs/${program.program_id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                  </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Employee Shift Details Section */}
      <Card sx={{ mt: { xs: 2, sm: 3 }, mb: 4 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant={isSmallMobile ? "subtitle1" : "h6"} component="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
            Employee Shift History
          </Typography>
          
          {shiftsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
              <CircularProgress size={30} />
            </Box>
          ) : employeeShifts.length === 0 ? (
            <Alert severity="info">
              No employee shift records found for this machine configuration.
            </Alert>
          ) : isMobile ? (
            // Mobile card-based layout
            <Stack spacing={2}>
              {employeeShifts.map((shift) => (
                <Paper 
                  key={shift.id} 
                  variant="outlined" 
                  sx={{ 
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => navigate(`/employee-shifts/${shift.employee_shift_id}`)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {shift.employee_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(shift.shift_date).toLocaleDateString()} • {shift.shift_time_display}
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/employee-shifts/${shift.employee_shift_id}`);
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Program
                      </Typography>
                      <Chip 
                        label={shift.company_name}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/programs/${shift.program_id}`);
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Production
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatInteger(shift.rolls)} rolls • {shift.weight} kg
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            // Desktop table layout
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Shift</strong></TableCell>
                    <TableCell><strong>Employee</strong></TableCell>
                    <TableCell><strong>Program</strong></TableCell>
                    <TableCell align="right"><strong>Rolls</strong></TableCell>
                    <TableCell align="right"><strong>Weight (kg)</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeShifts.map((shift) => (
                    <TableRow key={shift.id}>
                      <TableCell>
                        {new Date(shift.shift_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={shift.shift_time === 'day' ? '☀️ Day' : '🌙 Night'}
                          size="small"
                          variant="outlined"
                          color={shift.shift_time === 'day' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{shift.employee_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={shift.company_name}
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => navigate(`/programs/${shift.program_id}`)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell align="right">{formatInteger(shift.rolls)}</TableCell>
                      <TableCell align="right">{shift.weight}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/employee-shifts/${shift.employee_shift_id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}